import { html, type TemplateResult } from 'lit';
import type { NotificationCategory, NotificationOption } from './db/UserNotification.js';
import { NOTIFICATION_CATEGORY_LABELS } from './db/UserNotification.js';

/**
 * The question mail: an internal note back to the Hoberg & Driesch employee who forwarded the
 * request — never to the customer.
 *
 * The numbering and the option letters are there so a person can *refer* to a point ("beim
 * zweiten nehmen wir A") — not because anything has to be typed in a fixed shape. The reply is
 * read by a model against all the open questions at once, so free prose in any order is the
 * expected input. Asking for a format would only make people feel they had got it wrong.
 *
 * Pure and isomorphic: the caller passes the absolute link, since only the server knows the
 * URL base.
 */

export interface QuestionMailItem {
  index: number;
  category: NotificationCategory;
  title: string;
  body: string;
  positionNumber?: string | null;
  options: Array<Pick<NotificationOption, 'key' | 'label'>>;
}

/** `[#RF-<token>]` in the subject — the anchor that survives a server rewriting In-Reply-To. */
export const REPLY_TAG_PATTERN = /\[#RF-(?<token>[A-Za-z0-9]+)\]/;

export const buildReplySubjectTag = (token: string) => `[#RF-${token}]`;

export function buildQuestionMailSubject(originalSubject: string | null | undefined, token: string): string {
  const base = (originalSubject ?? '').trim() || 'Ihre weitergeleitete Anfrage';
  const withRe = /^re:/i.test(base) ? base : `Re: ${base}`;
  return `${withRe} ${buildReplySubjectTag(token)}`;
}

const headline = (item: QuestionMailItem) =>
  `${item.index}) ${item.positionNumber ? `Pos. ${item.positionNumber} — ` : ''}${item.title}`;

/**
 * Examples built from the real questions, deliberately in three different shapes — a bare
 * option letter, a sentence, and a combined answer. The point is to show that none of them is
 * the required one.
 */
function buildExample(items: QuestionMailItem[]): string[] {
  const withOption = items.find(item => item.options.length);
  const [first] = items;
  const examples: string[] = [];
  if (withOption) {
    const [option] = withOption.options;
    examples.push(`  "Bei Punkt ${withOption.index} ${option ? option.label.toLowerCase() : 'passt das so'}."`);
  }
  if (first && first !== withOption) examples.push(`  "Punkt ${first.index}: siehe unten, ich habe nachgefragt."`);
  examples.push('  "Passt alles bis auf den letzten Punkt, da brauchen wir 6 m."');
  return examples;
}

export function buildQuestionMailText(items: QuestionMailItem[], link: string): string {
  const blocks = items.flatMap(item => [
    headline(item),
    ...item.body.split('\n').map(line => `   ${line}`),
    ...(item.options.length
      ? ['   Zur Auswahl:', ...item.options.map(option => `     ${option.key} = ${option.label}`)]
      : []),
    '',
  ]);

  return [
    'Hallo,',
    '',
    `zu einer von Ihnen weitergeleiteten Anfrage sind ${items.length} Punkt(e) offen:`,
    '',
    ...blocks,
    'So antworten Sie:',
    'Antworten Sie einfach auf diese Mail — in eigenen Worten, in beliebiger Reihenfolge.',
    'Sie müssen kein Format einhalten und nicht alle Punkte beantworten. Beispiele, die alle',
    'funktionieren:',
    '',
    ...buildExample(items),
    '',
    'Lieber im Dashboard bearbeiten?',
    link,
    '',
    'Viele Grüße',
    'Eure Hoberg & Driesch Anfrage-Plattform',
  ].join('\n');
}

export interface ReplyConfirmation {
  /** One readable line per question the answer actually settled or changed. */
  handled: string[];
  /**
   * Assigned to a question, but not usable — an ambiguous customer, a material number that
   * exists nowhere. Kept apart from {@link unmatched} because the two mean opposite things to
   * the reader: this one says "we understood you and could not act", the other says "we did
   * not find what this refers to". Lumping them together made a precise objection look like a
   * parsing failure.
   */
  refused: string[];
  /** Sentences that referred to no question we asked. */
  unmatched: string[];
  /** Readings that were too uncertain to apply; they wait for a click in the dashboard. */
  pending: string[];
  sentToSap: boolean;
  stillOpen: number;
}

/**
 * The answer to the answer. Without it a mail reply disappears into the machine: the employee
 * has no way of telling whether "1: A" was understood as A, ignored, or silently misread.
 */
export function buildConfirmationText(confirmation: ReplyConfirmation, link: string): string {
  const lines = ['Hallo,', '', 'danke — wir haben Ihre Antwort verarbeitet:', ''];

  if (confirmation.handled.length) lines.push(...confirmation.handled.map(entry => `✓ ${entry}`), '');
  if (confirmation.pending.length) {
    lines.push(
      'Bei diesen Punkten sind wir uns nicht sicher genug, um sie ohne Ihr Zeichen zu übernehmen.',
      'Bitte kurz im Dashboard bestätigen:',
      ...confirmation.pending.map(entry => `? ${entry}`),
      '',
    );
  }
  if (confirmation.refused.length) {
    lines.push(
      'Das konnten wir nicht übernehmen — die Punkte bleiben offen:',
      ...confirmation.refused.map(entry => `- ${entry}`),
      '',
    );
  }
  if (confirmation.unmatched.length) {
    lines.push('Diese Zeilen konnten wir keiner Frage zuordnen:', ...confirmation.unmatched.map(entry => `- ${entry}`), '');
  }

  lines.push(
    confirmation.sentToSap
      ? 'Die Anfrage ist damit vollständig und wurde an SAP übermittelt.'
      : confirmation.stillOpen > 0
        ? `Damit sind noch ${confirmation.stillOpen} Punkt(e) offen.`
        : 'Es sind keine Punkte mehr offen.',
  );
  // Answering is the intended way through, so the acknowledgement has to say so. It is the
  // last mail in the thread and therefore the one people reply to — pointing only at the
  // dashboard would send them somewhere they may not go.
  if (confirmation.stillOpen > 0) {
    lines.push('', 'Sie können die offenen Punkte direkt auf diese Mail beantworten.');
  }
  lines.push(
    '',
    'Oder im Dashboard bearbeiten:',
    link,
    '',
    'Viele Grüße',
    'Eure Hoberg & Driesch Anfrage-Plattform',
  );
  return lines.join('\n');
}

export function buildConfirmationHtml(confirmation: ReplyConfirmation, link: string): TemplateResult {
  return html`
    <div style="background:#f4f5f7;padding:24px 12px;font-family:Arial,Helvetica,sans-serif;">
      <div style="max-width:560px;margin:0 auto;background:#ffffff;border-radius:8px;border:1px solid #e3e6ea;padding:28px;color:#243240;font-size:14px;line-height:1.55;">
        <p style="margin:0 0 14px;">Hallo,</p>
        <p style="margin:0 0 14px;">danke — wir haben Ihre Antwort verarbeitet:</p>
        ${confirmation.handled.length
          ? html`<ul style="margin:0 0 16px;padding-left:18px;color:#1f6b3a;">
              ${confirmation.handled.map(entry => html`<li style="margin:4px 0;">${entry}</li>`)}
            </ul>`
          : null}
        ${confirmation.pending.length
          ? html`<div style="background:#eef4fb;border-left:4px solid #1f3a5f;padding:12px 16px;margin:0 0 16px;">
              <b>Bitte kurz bestätigen</b>
              <ul style="margin:6px 0 0;padding-left:18px;">
                ${confirmation.pending.map(entry => html`<li style="margin:4px 0;">${entry}</li>`)}
              </ul>
            </div>`
          : null}
        ${confirmation.refused.length
          ? html`<div style="background:#fdf3f2;border-left:4px solid #d9534f;padding:12px 16px;margin:0 0 16px;">
              <b>Nicht übernommen — bleibt offen</b>
              <ul style="margin:6px 0 0;padding-left:18px;">
                ${confirmation.refused.map(entry => html`<li style="margin:4px 0;">${entry}</li>`)}
              </ul>
            </div>`
          : null}
        ${confirmation.unmatched.length
          ? html`<div style="background:#fdf6e7;border-left:4px solid #d08a1f;padding:12px 16px;margin:0 0 16px;">
              <b>Keiner Frage zuordenbar</b>
              <ul style="margin:6px 0 0;padding-left:18px;">
                ${confirmation.unmatched.map(entry => html`<li style="margin:4px 0;">${entry}</li>`)}
              </ul>
            </div>`
          : null}
        <p style="margin:0 0 8px;">
          ${confirmation.sentToSap
            ? 'Die Anfrage ist damit vollständig und wurde an SAP übermittelt.'
            : confirmation.stillOpen > 0
              ? `Damit sind noch ${confirmation.stillOpen} Punkt(e) offen.`
              : 'Es sind keine Punkte mehr offen.'}
        </p>
        ${confirmation.stillOpen > 0
          ? html`<p style="margin:0 0 18px;">
              <b>Sie können die offenen Punkte direkt auf diese Mail beantworten.</b>
            </p>`
          : null}
        <a
          href="${link}"
          style="display:inline-block;background:#1f3a5f;color:#ffffff;text-decoration:none;padding:12px 24px;border-radius:6px;font-weight:bold;font-size:14px;"
          >Anfrage öffnen</a
        >
      </div>
    </div>
  `;
}

function renderItem(item: QuestionMailItem): TemplateResult {
  return html`
    <div style="border-top:1px solid #e3e6ea;padding:14px 0;">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.4px;color:#7a8896;margin:0 0 4px;">
        ${NOTIFICATION_CATEGORY_LABELS[item.category]}
      </div>
      <div style="font-weight:bold;color:#243240;margin:0 0 6px;">${headline(item)}</div>
      <div style="color:#4a5766;white-space:pre-line;margin:0 0 8px;">${item.body}</div>
      ${item.options.length
        ? html`<div style="background:#f4f6f8;border-radius:4px;padding:8px 12px;">
            ${item.options.map(
              option =>
                html`<div style="margin:2px 0;color:#243240;">
                  <b>${option.key}</b> = ${option.label}
                </div>`,
            )}
          </div>`
        : null}
    </div>
  `;
}

export function buildQuestionMailHtml(items: QuestionMailItem[], link: string): TemplateResult {
  return html`
    <div style="background:#f4f5f7;padding:24px 12px;font-family:Arial,Helvetica,sans-serif;">
      <div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e3e6ea;">
        <div style="background:#1f3a5f;padding:20px 28px;">
          <div style="color:#ffffff;font-size:18px;font-weight:bold;letter-spacing:0.3px;">
            Hoberg &amp; Driesch · Anfrage-Plattform
          </div>
        </div>
        <div style="padding:28px;color:#243240;font-size:14px;line-height:1.55;">
          <p style="margin:0 0 14px;">Hallo,</p>
          <p style="margin:0 0 8px;">
            zu einer von Ihnen weitergeleiteten Anfrage sind ${items.length} Punkt(e) offen:
          </p>
          ${items.map(renderItem)}
          <div style="background:#eef4fb;border-left:4px solid #1f3a5f;border-radius:4px;padding:12px 16px;margin:18px 0;">
            <div style="font-weight:bold;margin:0 0 6px;">So antworten Sie</div>
            <div style="color:#4a5766;">
              Antworten Sie einfach auf diese Mail — in eigenen Worten, in beliebiger Reihenfolge. Sie müssen
              kein Format einhalten und nicht alle Punkte beantworten. Zum Beispiel:
            </div>
            <pre style="margin:8px 0 0;font-family:Consolas,monospace;color:#243240;">
${buildExample(items).join('\n')}</pre
            >
          </div>
          <a
            href="${link}"
            style="display:inline-block;background:#1f3a5f;color:#ffffff;text-decoration:none;padding:12px 24px;border-radius:6px;font-weight:bold;font-size:14px;"
            >Im Dashboard bearbeiten</a
          >
        </div>
        <div style="padding:16px 28px;background:#f0f2f5;color:#7a8896;font-size:12px;">
          Automatische Nachricht der Hoberg &amp; Driesch Anfrage-Plattform.
        </div>
      </div>
    </div>
  `;
}
